import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Building2, Mail, Phone, MapPin, Clock, AlertCircle, Edit2, Save, Loader, ChefHat, DollarSign, FileText, Beer, PlusCircle, Star, Music, Tv, Users, Heart, Globe, Utensils, Image } from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { Database } from '../lib/database.types';
import { useLanguage } from '../contexts/LanguageContext';
import { getRestaurantPicturesByCategory } from '../lib/restaurants';
import BackButton from '../components/BackButton';

type Restaurant = Database['public']['Tables']['restaurants']['Row'];
type Bar = Database['public']['Tables']['bars']['Row'];
type Venue = Restaurant | Bar & { type: 'restaurant' | 'bar' };

const MyVenues = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);
  const [venuePictures, setVenuePictures] = useState<{
    gallery: string[];
    menu: string[];
    interior: string[];
    exterior: string[];
  }>({
    gallery: [],
    menu: [],
    interior: [],
    exterior: []
  });
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    opening_hours: '',
    closing_hours: '',
    cuisine_type: '',
    bar_type: '',
    description: '',
    price_range: '',
    features: {
      sports_tv: false,
      live_music: false,
      outdoor_seating: false,
      pool_tables: false,
      dart_boards: false,
      karaoke: false,
      happy_hour: false,
      food_service: false,
      smoking_allowed: false,
      wheelchair_accessible: false,
      parking_available: false,
      reservations_accepted: false
    }
  });

  useEffect(() => {
    fetchVenues();
  }, []);

  useEffect(() => {
    if (selectedVenue) {
      fetchVenuePictures(selectedVenue.id, selectedVenue.type);
    }
  }, [selectedVenue]);

  const fetchVenues = async () => {
    try {
      setLoading(true);
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;
      if (!user) {
        navigate('/login');
        return;
      }

      // Fetch restaurants
      const { data: restaurants, error: restaurantsError } = await supabase
        .from('restaurants')
        .select()
        .eq('owner_id', user.id)
        .order('created_at', { ascending: false });

      if (restaurantsError) throw restaurantsError;

      // Fetch bars
      const { data: bars, error: barsError } = await supabase
        .from('bars')
        .select()
        .eq('owner_id', user.id)
        .order('created_at', { ascending: false });

      if (barsError) throw barsError;

      // Log the retrieved venues for debugging
      console.log('Fetched restaurants:', restaurants);
      console.log('Fetched bars:', bars);

      const allVenues = [
        ...(restaurants || []).map(r => ({ ...r, type: 'restaurant' as const })),
        ...(bars || []).map(b => ({ ...b, type: 'bar' as const }))
      ].sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      setVenues(allVenues);
      if (allVenues.length > 0) {
        setSelectedVenue(allVenues[0]);
        updateFormData(allVenues[0]);
      }
    } catch (err) {
      console.error('Error fetching venues:', err);
      setError(err instanceof Error ? err.message : 'Failed to load venues');
    } finally {
      setLoading(false);
    }
  };

  const fetchVenuePictures = async (venueId: string, venueType: 'restaurant' | 'bar') => {
    try {
      if (venueType === 'restaurant') {
        // Use the new function to get pictures from restaurant_pictures table
        const pictures = await getRestaurantPicturesByCategory(venueId);
        setVenuePictures(pictures);
      } else {
        // For bars, use the old method
        const { data, error } = await supabase
          .from('bars')
          .select('pictures')
          .eq('id', venueId)
          .single();

        if (error) throw error;

        // Normalize the pictures object to ensure all arrays exist
        setVenuePictures({
          gallery: Array.isArray(data.pictures?.gallery) ? data.pictures.gallery : [],
          menu: Array.isArray(data.pictures?.menu) ? data.pictures.menu : [],
          interior: Array.isArray(data.pictures?.interior) ? data.pictures.interior : [],
          exterior: Array.isArray(data.pictures?.exterior) ? data.pictures.exterior : []
        });
      }
    } catch (err) {
      console.error('Error fetching venue pictures:', err);
      setVenuePictures({
        gallery: [],
        menu: [],
        interior: [],
        exterior: []
      });
    }
  };

  const updateFormData = (venue: Venue) => {
    setFormData({
      name: venue.name || '',
      email: venue.email || '',
      phone: venue.phone || '',
      address: venue.address || '',
      opening_hours: venue.opening_hours || '',
      closing_hours: 'closing_hours' in venue ? venue.closing_hours || '' : '',
      cuisine_type: 'cuisine_type' in venue ? venue.cuisine_type || '' : '',
      bar_type: 'bar_type' in venue ? venue.bar_type || '' : '',
      description: venue.description || '',
      price_range: venue.price_range || '',
      features: 'features' in venue ? venue.features : formData.features
    });
  };

  const handleVenueSelect = (venue: Venue) => {
    setSelectedVenue(venue);
    updateFormData(venue);
    setEditing(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFeatureToggle = (feature: string) => {
    setFormData(prev => ({
      ...prev,
      features: {
        ...prev.features,
        [feature]: !prev.features[feature as keyof typeof prev.features]
      }
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedVenue) return;

    try {
      setError(null);

      if (selectedVenue.type === 'restaurant') {
        const { error: updateError } = await supabase
          .from('restaurants')
          .update({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            address: formData.address,
            opening_hours: formData.opening_hours,
            cuisine_type: formData.cuisine_type,
            description: formData.description,
            price_range: formData.price_range
          })
          .eq('id', selectedVenue.id);

        if (updateError) throw updateError;
      } else {
        const { error: updateError } = await supabase
          .from('bars')
          .update({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            address: formData.address,
            opening_hours: formData.opening_hours,
            closing_hours: formData.closing_hours,
            bar_type: formData.bar_type,
            description: formData.description,
            price_range: formData.price_range,
            features: formData.features
          })
          .eq('id', selectedVenue.id);

        if (updateError) throw updateError;
      }

      setEditing(false);
      await fetchVenues();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update venue');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader className="h-12 w-12 animate-spin text-orange-500 mx-auto mb-4" />
          <p className="text-gray-600 animate-pulse">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-lg flex items-center">
          <AlertCircle className="h-6 w-6 mr-3 flex-shrink-0" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <BackButton />
      {/* Hero Section */}
      <div className="relative mb-12 rounded-2xl overflow-hidden">
        <div 
          className="h-48 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1552566626-52f8b828add9?q=80&w=2000&auto=format&fit=crop')"
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-white">
              <h1 className="text-4xl md:text-5xl font-bold mb-4 transform transition-all duration-500 hover:scale-105">
                {t('myVenues.title')}
              </h1>
              <p className="text-xl text-gray-200 max-w-2xl mx-auto">
                {t('myVenues.subtitle')}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-8">
        {/* Venues List */}
        <div className="lg:col-span-4">
          <div className="bg-white rounded-xl shadow-lg p-6 sticky top-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">{t('myVenues.title')}</h2>
              <Link
                to="/restaurant-signup"
                className="flex items-center space-x-2 bg-orange-500 text-white px-4 py-2 rounded-xl hover:bg-orange-600 transition transform hover:scale-105"
              >
                <PlusCircle className="h-5 w-5" />
                <span>{t('myVenues.addNew')}</span>
              </Link>
            </div>

            <div className="space-y-4">
              {venues.map((venue) => (
                <button
                  key={venue.id}
                  onClick={() => handleVenueSelect(venue)}
                  className={`w-full text-left p-4 rounded-xl border transition transform hover:scale-[1.02] ${
                    selectedVenue?.id === venue.id
                      ? 'border-orange-500 bg-orange-50 shadow-md'
                      : 'border-gray-200 hover:border-orange-300 hover:bg-orange-50'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{venue.name}</h3>
                      <div className="flex items-center space-x-2 text-gray-600 mt-1">
                        {venue.type === 'restaurant' ? (
                          <ChefHat className="h-4 w-4" />
                        ) : (
                          <Beer className="h-4 w-4" />
                        )}
                        <span>
                          {venue.type === 'restaurant' 
                            ? venue.cuisine_type || 'Restaurant'
                            : venue.bar_type || 'Bar'
                          }
                        </span>
                        {venue.price_range && (
                          <>
                            <span>•</span>
                            <span>{venue.price_range}</span>
                          </>
                        )}
                      </div>
                      {venue.address && (
                        <div className="flex items-center space-x-2 text-gray-600 mt-1">
                          <MapPin className="h-4 w-4" />
                          <span className="text-sm">{venue.address}</span>
                        </div>
                      )}
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      venue.status === 'approved'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {venue.status === 'approved' ? t('myVenues.approved') : t('myVenues.pending')}
                    </span>
                  </div>
                </button>
              ))}

              {venues.length === 0 && (
                <div className="text-center py-8 bg-gray-50 rounded-xl">
                  <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{t('myVenues.noVenues')}</h3>
                  <p className="text-gray-600 mb-4">
                    {t('myVenues.noVenuesMessage')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Selected Venue Details */}
        {selectedVenue && (
          <div className="lg:col-span-8">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              {/* Header */}
              <div className="p-6 bg-gradient-to-r from-orange-500 to-orange-600">
                <div className="flex justify-between items-center">
                  <div className="text-white">
                    <h1 className="text-2xl font-bold">{selectedVenue.name}</h1>
                    <p className="mt-2 text-orange-100">
                      {selectedVenue.type === 'restaurant' ? t('venue.register.restaurant') : t('venue.register.bar')}
                    </p>
                  </div>
                  <button
                    onClick={() => setEditing(!editing)}
                    className="flex items-center space-x-2 bg-white text-orange-500 px-4 py-2 rounded-xl hover:bg-orange-50 transition transform hover:scale-105"
                  >
                    {editing ? (
                      <>
                        <Save className="h-5 w-5" />
                        <span>{t('myVenues.saveChanges')}</span>
                      </>
                    ) : (
                      <>
                        <Edit2 className="h-5 w-5" />
                        <span>{t('myVenues.editDetails')}</span>
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <form onSubmit={handleSubmit} className="space-y-8">
                  {/* Pictures Section */}
                  <div className="bg-indigo-50 p-6 rounded-xl">
                    <div className="flex items-center space-x-2 text-indigo-700 mb-6">
                      <Image className="h-6 w-6" />
                      <h2 className="text-xl font-semibold">{t('myVenues.pictures')}</h2>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {venuePictures.exterior.map((url, index) => (
                        <div key={`exterior-${index}`} className="relative aspect-square rounded-lg overflow-hidden">
                          <img 
                            src={url} 
                            alt={`${selectedVenue.name} exterior ${index + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              console.error(`Failed to load image: ${url}`);
                              (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=300&auto=format&fit=crop';
                            }}
                          />
                          <div className="absolute top-2 left-2 px-2 py-1 bg-black/50 text-white text-xs rounded">
                            {t('myVenues.exterior')}
                          </div>
                        </div>
                      ))}

                      {venuePictures.interior.map((url, index) => (
                        <div key={`interior-${index}`} className="relative aspect-square rounded-lg overflow-hidden">
                          <img 
                            src={url} 
                            alt={`${selectedVenue.name} interior ${index + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              console.error(`Failed to load image: ${url}`);
                              (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=300&auto=format&fit=crop';
                            }}
                          />
                          <div className="absolute top-2 left-2 px-2 py-1 bg-black/50 text-white text-xs rounded">
                            {t('myVenues.interior')}
                          </div>
                        </div>
                      ))}

                      {venuePictures.menu.map((url, index) => (
                        <div key={`menu-${index}`} className="relative aspect-square rounded-lg overflow-hidden">
                          <img 
                            src={url} 
                            alt={`${selectedVenue.name} menu ${index + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              console.error(`Failed to load image: ${url}`);
                              (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=300&auto=format&fit=crop';
                            }}
                          />
                          <div className="absolute top-2 left-2 px-2 py-1 bg-black/50 text-white text-xs rounded">
                            {t('myVenues.menu')}
                          </div>
                        </div>
                      ))}

                      {venuePictures.gallery.map((url, index) => (
                        <div key={`gallery-${index}`} className="relative aspect-square rounded-lg overflow-hidden">
                          <img 
                            src={url} 
                            alt={`${selectedVenue.name} gallery ${index + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              console.error(`Failed to load image: ${url}`);
                              (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=300&auto=format&fit=crop';
                            }}
                          />
                          <div className="absolute top-2 left-2 px-2 py-1 bg-black/50 text-white text-xs rounded">
                            {t('myVenues.gallery')}
                          </div>
                        </div>
                      ))}
                      
                      {/* Show message if no pictures */}
                      {(!venuePictures.exterior.length && 
                        !venuePictures.interior.length && 
                        !venuePictures.menu.length && 
                        !venuePictures.gallery.length) && (
                        <div className="col-span-4 py-8 text-center text-gray-500">
                          <Image className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                          <p>{t('myVenues.noPictures')}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Basic Information */}
                  <div className="bg-orange-50 p-6 rounded-xl">
                    <div className="flex items-center space-x-2 text-orange-700 mb-6">
                      <Building2 className="h-6 w-6" />
                      <h2 className="text-xl font-semibold">{t('myVenues.basicInfo')}</h2>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <div className="flex items-center space-x-2">
                            <Building2 className="h-4 w-4" />
                            <span>{t('myVenues.venueName')}</span>
                          </div>
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          disabled={!editing}
                          className="w-full p-3 border rounded-xl disabled:bg-gray-50 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <div className="flex items-center space-x-2">
                            <Mail className="h-4 w-4" />
                            <span>{t('myVenues.email')}</span>
                          </div>
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          disabled={!editing}
                          className="w-full p-3 border rounded-xl disabled:bg-gray-50 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <div className="flex items-center space-x-2">
                            <Phone className="h-4 w-4" />
                            <span>{t('myVenues.phone')}</span>
                          </div>
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          disabled={!editing}
                          className="w-full p-3 border rounded-xl disabled:bg-gray-50 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <div className="flex items-center space-x-2">
                            <MapPin className="h-4 w-4" />
                            <span>{t('myVenues.address')}</span>
                          </div>
                        </label>
                        <input
                          type="text"
                          name="address"
                          value={formData.address}
                          onChange={handleInputChange}
                          disabled={!editing}
                          className="w-full p-3 border rounded-xl disabled:bg-gray-50 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Venue Details */}
                  <div className="bg-blue-50 p-6 rounded-xl">
                    <div className="flex items-center space-x-2 text-blue-700 mb-6">
                      {selectedVenue.type === 'restaurant' ? (
                        <ChefHat className="h-6 w-6" />
                      ) : (
                        <Beer className="h-6 w-6" />
                      )}
                      <h2 className="text-xl font-semibold">{t('myVenues.venueDetails')}</h2>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <div className="flex items-center space-x-2">
                            <Clock className="h-4 w-4" />
                            <span>{t('myVenues.openingHours')}</span>
                          </div>
                        </label>
                        <input
                          type="text"
                          name="opening_hours"
                          value={formData.opening_hours}
                          onChange={handleInputChange}
                          disabled={!editing}
                          placeholder="e.g., Mon-Sun: 11:00 AM - 10:00 PM"
                          className="w-full p-3 border rounded-xl disabled:bg-gray-50 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        />
                      </div>

                      {selectedVenue.type === 'bar' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            <div className="flex items-center space-x-2">
                              <Clock className="h-4 w-4" />
                              <span>{t('myVenues.closingHours')}</span>
                            </div>
                          </label>
                          <input
                            type="text"
                            name="closing_hours"
                            value={formData.closing_hours}
                            onChange={handleInputChange}
                            disabled={!editing}
                            placeholder="e.g., 2:00 AM"
                            className="w-full p-3 border rounded-xl disabled:bg-gray-50 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          />
                        </div>
                      )}

                      {selectedVenue.type === 'restaurant' ? (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            <div className="flex items-center space-x-2">
                              <Utensils className="h-4 w-4" />
                              <span>{t('myVenues.cuisineType')}</span>
                            </div>
                          </label>
                          <select
                            name="cuisine_type"
                            value={formData.cuisine_type}
                            onChange={handleInputChange}
                            disabled={!editing}
                            className="w-full p-3 border rounded-xl disabled:bg-gray-50 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          >
                            <option value="">Select cuisine type</option>
                            <option value="italian">Italian</option>
                            <option value="japanese">Japanese</option>
                            <option value="mexican">Mexican</option>
                            <option value="indian">Indian</option>
                            <option value="american">American</option>
                            <option value="chinese">Chinese</option>
                            <option value="french">French</option>
                            <option value="mediterranean">Mediterranean</option>
                            <option value="thai">Thai</option>
                            <option value="vietnamese">Vietnamese</option>
                          </select>
                        </div>
                      ) : (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            <div className="flex items-center space-x-2">
                              <Beer className="h-4 w-4" />
                              <span>{t('myVenues.barType')}</span>
                            </div>
                          </label>
                          <select
                            name="bar_type"
                            value={formData.bar_type}
                            onChange={handleInputChange}
                            disabled={!editing}
                            className="w-full p-3 border rounded-xl disabled:bg-gray-50 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          >
                            <option value="">Select bar type</option>
                            <option value="pub">Pub</option>
                            <option value="sports_bar">Sports Bar</option>
                            <option value="cocktail_lounge">Cocktail Lounge</option>
                            <option value="wine_bar">Wine Bar</option>
                            <option value="brewery">Brewery</option>
                            <option value="dive_bar">Dive Bar</option>
                            <option value="karaoke_bar">Karaoke Bar</option>
                            <option value="nightclub">Nightclub</option>
                          </select>
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <div className="flex items-center space-x-2">
                            <DollarSign className="h-4 w-4" />
                            <span>{t('myVenues.priceRange')}</span>
                          </div>
                        </label>
                        <select
                          name="price_range"
                          value={formData.price_range}
                          onChange={handleInputChange}
                          disabled={!editing}
                          className="w-full p-3 border rounded-xl disabled:bg-gray-50 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        >
                          <option value="">Select price range</option>
                          <option value="$">$ (Budget-friendly)</option>
                          <option value="$$">$$ (Moderate)</option>
                          <option value="$$$">$$$ (Upscale)</option>
                          <option value="$$$$">$$$$ (Luxury)</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Bar Features */}
                  {selectedVenue.type === 'bar' && (
                    <div className="bg-green-50 p-6 rounded-xl">
                      <div className="flex items-center space-x-2 text-green-700 mb-6">
                        <Star className="h-6 w-6" />
                        <h2 className="text-xl font-semibold">{t('myVenues.features')}</h2>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {Object.entries(formData.features).map(([feature, enabled]) => (
                          <label key={feature} className="flex items-center space-x-3 p-3 bg-white rounded-xl shadow-sm">
                            <input
                              type="checkbox"
                              checked={enabled}
                              onChange={() => editing && handleFeatureToggle(feature)}
                              disabled={!editing}
                              className="rounded text-orange-500 focus:ring-orange-500 disabled:opacity-50 h-5 w-5"
                            />
                            <span className="text-sm text-gray-700">
                              {feature.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Description */}
                  <div className="bg-purple-50 p-6 rounded-xl">
                    <div className="flex items-center space-x-2 text-purple-700 mb-6">
                      <FileText className="h-6 w-6" />
                      <h2 className="text-xl font-semibold">{t('myVenues.description')}</h2>
                    </div>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      disabled={!editing}
                      rows={4}
                      className="w-full p-3 border rounded-xl disabled:bg-gray-50 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="Tell customers about your venue..."
                    />
                  </div>

                  {/* Status Information */}
                  <div className="bg-gray-50 p-6 rounded-xl">
                    <div className="flex items-center space-x-2 text-gray-700 mb-6">
                      <Globe className="h-6 w-6" />
                      <h2 className="text-xl font-semibold">{t('myVenues.statusInfo')}</h2>
                    </div>
                    <div className="grid grid-cols-2 gap-6">
                      <div className="bg-white p-4 rounded-xl shadow-sm">
                        <span className="text-sm text-gray-500">{t('myVenues.registrationDate')}</span>
                        <p className="text-lg font-medium text-gray-900">
                          {new Date(selectedVenue.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="bg-white p-4 rounded-xl shadow-sm">
                        <span className="text-sm text-gray-500">{t('myVenues.status')}</span>
                        <p className={`text-lg font-medium ${
                          selectedVenue.status === 'approved' 
                            ? 'text-green-600'
                            : 'text-yellow-600'
                        }`}>
                          {selectedVenue.status === 'approved' ? t('myVenues.approved') : t('myVenues.pending')}
                        </p>
                      </div>
                    </div>
                  </div>

                  {editing && (
                    <div className="flex justify-end space-x-4 pt-6">
                      <button
                        type="button"
                        onClick={() => setEditing(false)}
                        className="px-6 py-3 border rounded-xl text-gray-600 hover:bg-gray-50 transition transform hover:scale-105"
                      >
                        {t('myVenues.cancel')}
                      </button>
                      <button
                        type="submit"
                        className="px-8 py-3 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition transform hover:scale-105 flex items-center space-x-2"
                      >
                        <Save className="h-5 w-5" />
                        <span>{t('myVenues.saveChanges')}</span>
                      </button>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyVenues;